/**
 * BossMissile.ts
 * Pooled homing missile fired by the Void Mothership during HOLD.
 *
 * Sibling to `BossProjectile` (the turret bolt) — same pool contract,
 * but the kinematics differ:
 *   - Heading is a scalar angle (radians) that rotates toward the live
 *     ship position each tick at up to `..._MISSILE_TURN_RATE_RAD_S`.
 *   - Linear speed is constant (`..._MISSILE_SPEED`) — only direction
 *     changes over time, so the projectile reads as a guided weapon
 *     rather than a smart accelerator.
 *   - Despawns on either an offscreen-LEFT cull (the missile overshot
 *     the ship and exited the viewport) or a TTL expiry (the ship
 *     juked the missile into a permanent orbit) — without the TTL
 *     guard a missile that can't quite turn fast enough would loop
 *     forever and pin the pool.
 *
 * Pool contract: implements `reset()` for `ObjectPool` recycling.
 * Always acquire from `bossMissilePool` (see `bossMissileBarrage.ts`).
 *
 * Ship-damage collision is handled in `shipDamage.ts` against the
 * `boss-missile` body kind — see `shipDamage.bodies.test.ts` for
 * coverage. This file owns kinematics + pool lifecycle only.
 *
 * NO React imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift (boss Phase 3 — homing missiles).
 *
 * @module BossMissile
 */

import { BaseEntity } from './BaseEntity';
import type { LevelRuntime } from '../engine/gameState';
import { spaceState } from '../engine/spaceState';
import {
  VOID_MOTHERSHIP_MISSILE_WIDTH,
  VOID_MOTHERSHIP_MISSILE_HEIGHT,
  VOID_MOTHERSHIP_MISSILE_SPEED,
  VOID_MOTHERSHIP_MISSILE_TURN_RATE_RAD_S,
  VOID_MOTHERSHIP_MISSILE_TTL_S,
} from '../../constants/bossConstants';

/**
 * Offscreen-LEFT cull margin (px) before a missile is recycled.
 * Mirrors `BOSS_PROJECTILE_OFFSCREEN_MARGIN` so a missile that has just
 * cleared the viewport edge isn't released mid-frame while still
 * partially visible.
 */
export const BOSS_MISSILE_OFFSCREEN_MARGIN = 32;

/**
 * BossMissile — pooled homing missile launched from the boss's
 * forward-left edge. Heading is a scalar angle so the homing update is
 * a single clamped delta-angle rather than a 2D vector lerp; the
 * kinematic integration then uses `(cos, sin)` to derive `(vx, vy)`.
 */
export class BossMissile extends BaseEntity {
  /** Monotonic ID counter for stable React keys across pool recycling. */
  private static nextId = 1;

  /** Unique spawn identifier — re-issued each `spawn()` call. */
  public spawnId = 0;

  /** True while this missile is actively travelling. */
  public active = false;

  /** Current heading in radians. 0 = east, π = west. Spawned at π so
   *  the missile starts travelling LEFT toward the ship. */
  public heading = Math.PI;

  /** Seconds of TTL remaining. Hits zero → missile dies regardless of
   *  position; bounds the pool against missiles that can't catch the
   *  ship and would otherwise orbit forever. */
  public ttl = 0;

  /** Seconds until the next trail-burst VFX spawn. Ticked by
   *  `updateBossMissiles` only while the missile is alive. Reset to 0
   *  on `spawn()` so the first update tick drops a burst — note that
   *  spawn happens *after* `update(dt)` integrates one frame of
   *  motion, so the first burst lands at the missile's position one
   *  dt after launch (~3 px at 60 fps), not the exact muzzle
   *  position. Accumulator-style (`+= INTERVAL`) so a dt hitch
   *  doesn't drift the trail cadence. */
  public trailTimer = 0;

  constructor() {
    super(0, 0, VOID_MOTHERSHIP_MISSILE_WIDTH, VOID_MOTHERSHIP_MISSILE_HEIGHT);
    this.dead = true;
  }

  /**
   * Initialise the missile at a world position with a heading pointing
   * left (the boss's forward direction).
   *
   * @param x - Spawn X in world space.
   * @param y - Spawn Y in world space.
   */
  public spawn(x: number, y: number): void {
    this.spawnId = BossMissile.nextId++;
    this.x = x;
    this.y = y;
    this.heading = Math.PI; // Due west — toward the ship's side.
    this.vx = -VOID_MOTHERSHIP_MISSILE_SPEED;
    this.vy = 0;
    this.ttl = VOID_MOTHERSHIP_MISSILE_TTL_S;
    this.trailTimer = 0;
    this.dead = false;
    this.active = true;
  }

  /** Reset state for pool recycling. Called by `ObjectPool.release()`. */
  public reset(): void {
    this.x = 0;
    this.y = 0;
    this.vx = 0;
    this.vy = 0;
    this.heading = Math.PI;
    this.ttl = 0;
    this.trailTimer = 0;
    this.dead = true;
    this.active = false;
  }

  /**
   * Advance one fixed timestep.
   *
   * Homing rule: compute the angular delta from the current heading to
   * the bearing-to-ship, clamp it to `±turnRate * dt`, then advance
   * `heading` by the clamped delta. With heading updated, `(vx, vy) =
   * speed * (cos, sin)` and position integrates linearly.
   *
   * @param dt    - Fixed timestep in seconds.
   * @param level - Unused; kept for `BaseEntity.update` contract.
   */
  public update(dt: number, level: LevelRuntime): void {
    void level;
    if (!this.active) return;

    // ── Homing turn (only when a ship target is available) ──
    const ship = spaceState.ship;
    if (ship !== null && !ship.dead) {
      const targetX = ship.x + ship.width / 2;
      const targetY = ship.y + ship.height / 2;
      const myCx = this.x + this.width / 2;
      const myCy = this.y + this.height / 2;
      const desiredHeading = Math.atan2(targetY - myCy, targetX - myCx);
      // DECISION: normalise the delta to (-π, π] so a small heading
      // jitter near the ±π wrap (missile facing nearly due-west, ship
      // crossing y=missile.y) doesn't trigger a full 2π turn the wrong
      // way around. Standard angle-wrap idiom.
      let delta = desiredHeading - this.heading;
      while (delta > Math.PI) delta -= 2 * Math.PI;
      while (delta < -Math.PI) delta += 2 * Math.PI;
      const maxStep = VOID_MOTHERSHIP_MISSILE_TURN_RATE_RAD_S * dt;
      if (delta > maxStep) delta = maxStep;
      else if (delta < -maxStep) delta = -maxStep;
      this.heading += delta;
    }

    // ── Kinematic integration ──
    this.vx = VOID_MOTHERSHIP_MISSILE_SPEED * Math.cos(this.heading);
    this.vy = VOID_MOTHERSHIP_MISSILE_SPEED * Math.sin(this.heading);
    this.x += this.vx * dt;
    this.y += this.vy * dt;

    // ── Cull paths ──
    this.ttl -= dt;
    if (this.ttl <= 0) {
      this.dead = true;
      this.active = false;
      return;
    }
    const leftCullX = spaceState.cameraX - BOSS_MISSILE_OFFSCREEN_MARGIN;
    if (this.x + this.width < leftCullX) {
      this.dead = true;
      this.active = false;
    }
  }
}
